import { Button, TYPE_BUTTON } from '@eventbrite/eds-button';
import { Icon } from '@eventbrite/eds-icon';
import { Ticket } from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { ValueOf } from 'type-fest';
import { WAITLIST_DISABLED } from '../../constants';
import { ConfirmStatus } from '../../constants/confirmStatus';
import { EventStatus } from '../../constants/eventStatus';
import { OrderStatus } from '../../constants/orderStatus';
import { WaitlistLinkStatus, WaitlistStatus } from '../../constants/waitlist';
import ConfirmDialog from './ConfirmDialog';

/**
 * @deprecated Use `ConfirmStatus.back` instead
 */
export const CONFIRM_BACK_STATUS = ConfirmStatus.back;
/**
 * @deprecated Use `ConfirmStatus.backToDeliveryMethod` instead
 */
export const CONFIRM_BACK_TO_DELIVERY_METHOD_STATUS =
    ConfirmStatus.backToDeliveryMethod;
/**
 * @deprecated Use `ConfirmStatus.backToPickASeat` instead
 */
export const CONFIRM_BACK_TO_PICK_A_SEAT_STATUS = ConfirmStatus.backToPickASeat;
/**
 * @deprecated Use `ConfirmStatus.backToReservedTicketConfirmation` instead
 */
export const CONFIRM_BACK_TO_RESERVED_TICKET_CONFIRMATION_STATUS =
    ConfirmStatus.backToReservedTicketConfirmation;
/**
 * @deprecated Use `ConfirmStatus.closeModal` instead
 */
export const CONFIRM_CLOSE_MODAL = ConfirmStatus.closeModal;
/**
 * @deprecated Use `ConfirmStatus.toTicketSelectionFromInterstitial` instead
 */
export const CONFIRM_TO_TICKET_SELECTION_FROM_INTERSTITIAL =
    ConfirmStatus.toTicketSelectionFromInterstitial;
/**
 * @deprecated Use `ConfirmStatus.close` instead
 */
export const CONFIRM_CLOSE_STATUS = ConfirmStatus.close;
/**
 * @deprecated Use `ConfirmStatus.closeToDeliveryMethod` instead
 */
export const CONFIRM_CLOSE_TO_DELIVERY_METHOD_STATUS =
    ConfirmStatus.closeToDeliveryMethod;
/**
 * @deprecated Use `ConfirmStatus.closeForLogin` instead
 */
export const CONFIRM_CLOSE_TO_LOGIN_STATUS = ConfirmStatus.closeForLogin;
/**
 * @deprecated Use `ConfirmStatus.closeToInterstitial` instead
 */
export const CONFIRM_CLOSE_TO_INTERSTITIAL_STATUS =
    ConfirmStatus.closeToInterstitial;
/**
 * @deprecated Use `ConfirmStatus.closeForPickASeat` instead
 */
export const CONFIRM_CLOSE_TO_PICK_A_SEAT_STATUS =
    ConfirmStatus.closeForPickASeat;
/**
 * @deprecated Use `ConfirmStatus.closeForReservedTicketConfirmation` instead
 */
export const CONFIRM_CLOSE_TO_RESERVED_TICKET_CONFIRMATION_STATUS =
    ConfirmStatus.closeForReservedTicketConfirmation;
/**
 * @deprecated Use `ConfirmStatus.toCheckout` instead
 */
export const CONFIRM_TO_CHECKOUT_STATUS = ConfirmStatus.toCheckout;
/**
 * @deprecated Use `ConfirmStatus.toTicketSelection` instead
 */
export const CONFIRM_TO_TICKET_SELECTION = ConfirmStatus.toTicketSelection;

export const SERVER_ERROR_STATUS = 'serverError';
export const TIME_EXPIRED_STATUS = 'timeExpired';
export const TIME_EXPIRED_STATUS_NO_BACK_BUTTON = 'timeExpiredNoBackButton';
/**
 * @deprecated Use `WaitlistStatus.error` instead
 */
export const WAITLIST_ERROR_STATUS = WaitlistStatus.error;
/**
 * @deprecated Use `WaitlistStatus.disabled` instead
 */
export const WAITLIST_DISABLED_STATUS = WaitlistStatus.disabled;

/**
 * @deprecated Use `OrderStatus.placed` instead
 */
export const ORDER_STATUS_PLACED = OrderStatus.placed;
/**
 * @deprecated Use `OrderStatus.processing` instead
 */
export const ORDER_STATUS_PROCESSING = OrderStatus.processing;
/**
 * @deprecated Use `OrderStatus.pending` instead
 */
export const ORDER_STATUS_PENDING = OrderStatus.pending;

export const STATUSES_TO_SHOW_ARROW_IN_MOBILE = [
    EventStatus.soldOut,
    EventStatus.soldOutInTicketSelection,
    EventStatus.soldOutWithCapacityWaitlist,
    EventStatus.soldOutWithTicketReseller,
    EventStatus.salesEnded,
    EventStatus.ended,
    EventStatus.canceled,
    EventStatus.unavailable,
    SERVER_ERROR_STATUS,
    WaitlistStatus.error,
    WaitlistStatus.disabled,
];

export const STATUS_CONTROL_ORDER_TIME_EXPIRED = [
    OrderStatus.processing,
    OrderStatus.placed,
];

export const ABANDON_ORDER_ATTEMPT_STATUSES = [
    ConfirmStatus.back,
    ConfirmStatus.backToDeliveryMethod,
    ConfirmStatus.closeForLogin,
    ConfirmStatus.backToPickASeat,
    ConfirmStatus.backToReservedTicketConfirmation,
    ConfirmStatus.close,
    ConfirmStatus.closeToDeliveryMethod,
    ConfirmStatus.closeForPickASeat,
    ConfirmStatus.closeForReservedTicketConfirmation,
    ConfirmStatus.toCheckout,
];

export const ERROR_TO_STATUS_PATH_MAPPING: Record<string, WaitlistStatus> = {
    [WaitlistLinkStatus.invalid]: WaitlistStatus.error,
    [WaitlistLinkStatus.expired]: WaitlistStatus.error,
    [WaitlistLinkStatus.used]: WaitlistStatus.error,
    [WaitlistLinkStatus.canceled]: WaitlistStatus.error,
    [WAITLIST_DISABLED]: WaitlistStatus.disabled,
};

export const ABANDON_ORDER_STATUSES = [
    ...ABANDON_ORDER_ATTEMPT_STATUSES,
    TIME_EXPIRED_STATUS,
];

export const DEFAULT_MESSAGE_TYPE = 'default';

export const DONATIONS_MAX_DAYS_AFTER_EVENT_END = 45;

const SOLD_OUT_CONTENT = {
    headerText: gettext('This event has sold out online'),
    children: gettext('This event is no longer available online.'),
};

const SOLD_OUT_IN_TICKET_SELECTION_CONTENT = {
    headerText: (
        <div>
            <div className="eds-l-mar-top-1 eds-l-mar-bot-2 eds-align--center">
                <Icon type={<Ticket />} size="medium" hasHalo={true} />
            </div>
            {gettext('This event has sold out')}
        </div>
    ),

    children: gettext(
        'Sadly, the event sold out while you were making your selection.',
    ),
};

const SALES_ENDED_CONTENT = {
    ...SOLD_OUT_CONTENT,
    headerText: gettext('Sales have ended online'),
};

const CANCELED_CONTENT = {
    headerText: gettext('This event has been canceled'),
    children: gettext('This event is no longer available.'),
};

const ENDED_CONTENT = {
    headerText: gettext('This event has ended'),
    children: gettext('This event is no longer available.'),
};

const EVENT_TICKETS_UNAVAILABLE_CONTENT = {
    headerText: gettext('Event currently unavailable'),
    children: gettext(
        'Registration is currently unavailable. Try waiting a minute and refresh the page.',
    ),
};

const SERVER_ERROR_CONTENT = {
    headerText: gettext('Something went wrong...'),
    children: gettext('Try refreshing the page and submitting the form again.'),
};

const WAITLIST_ERROR_CONTENT = {
    headerText: gettext('Your waitlist entry is not valid'),
    children: gettext('This event has sold out.'),
};

const WAITLIST_DISABLED_CONTENT = {
    ...WAITLIST_ERROR_CONTENT,
    headerText: gettext('Your waitlist entry is no longer available'),
};

const CALLBACKS_MAP = {
    [ConfirmStatus.backToDeliveryMethod]: 'toDeliveryMethod',
    [ConfirmStatus.backToPickASeat]: 'toPickASeat',
    [ConfirmStatus.backToReservedTicketConfirmation]:
        'toReservedTicketConfirmation',
    [ConfirmStatus.closeToDeliveryMethod]: 'toDeliveryMethod',
    [ConfirmStatus.closeToInterstitial]: 'toInterstitial',
    [ConfirmStatus.closeForLogin]: 'toLogin',
    [ConfirmStatus.closeForPickASeat]: 'toPickASeat',
    [ConfirmStatus.closeForReservedTicketConfirmation]:
        'toReservedTicketConfirmation',
    [ConfirmStatus.close]: 'toCheckout',
    [ConfirmStatus.closeModal]: 'closeModal',
    [ConfirmStatus.toTicketSelectionFromInterstitial]:
        'toTicketSelectionFromInterstitial',
    [ConfirmStatus.toCheckout]: 'toCheckout',
    [ConfirmStatus.toTicketSelection]: 'toTicketSelection',
};

const confirmPage = (
    availableCallbacks: Record<ValueOf<typeof CALLBACKS_MAP>, () => void>,
    confirmCallback: keyof typeof CALLBACKS_MAP = ConfirmStatus.toTicketSelection,
    cancelCallback: keyof typeof CALLBACKS_MAP = ConfirmStatus.toCheckout,
) => {
    const defaultCallback = () => undefined;
    const returnCancelCallbackName = CALLBACKS_MAP[cancelCallback];
    const returnConfirmCallbackName = CALLBACKS_MAP[confirmCallback];
    const returnCancelCallback =
        availableCallbacks[returnCancelCallbackName] || defaultCallback;
    const returnConfirmCallback =
        availableCallbacks[returnConfirmCallbackName] || defaultCallback;

    return {
        headerText: gettext('Leave Checkout?'),

        children: (
            <ConfirmDialog
                infoMessage={gettext(
                    "Are you sure you want to leave checkout? The items you've selected may not be available later.",
                )}
                cancelButtonText={gettext('Stay')}
                onCancelButtonClick={returnCancelCallback}
                confirmButtonText={gettext('Leave')}
                onConfirmButtonClick={returnConfirmCallback}
            />
        ),
    };
};

export const timeExpiredContent = ({
    toTicketSelection,
}: {
    toTicketSelection: () => void;
}) => ({
    headerText: gettext('Time Limit Reached'),

    children: (
        <div>
            <div className="eds-l-pad-bot-2" tabIndex={0}>
                {gettext(
                    'Your reservation has been released. Please re-start your purchase.',
                )}
            </div>
            <Button
                type={TYPE_BUTTON}
                style="link"
                onClick={toTicketSelection}
                tabIndex="0"
            >
                {gettext('Back to Tickets')}
            </Button>
        </div>
    ),

    alertText: gettext(
        'Your cart timer has expired. Please re-start your purchase.',
    ),
});

export const timeExpiredContentNoBackButton = () => ({
    headerText: gettext('Time Limit Reached'),

    children: (
        <div>
            <div className="eds-l-pad-bot-2">
                {gettext(
                    'Your reservation has been released. Please re-start your purchase.',
                )}
            </div>
        </div>
    ),

    alertText: gettext(
        'Your cart timer has expired. Please re-start your purchase.',
    ),
});

export const STATUS_CONTENT_MAP = {
    [ConfirmStatus.back]: confirmPage,
    [ConfirmStatus.backToDeliveryMethod]: (
        availableCallbacks: Parameters<typeof confirmPage>[0],
        confirmCallback: Parameters<typeof confirmPage>[1],
    ) =>
        confirmPage(
            availableCallbacks,
            confirmCallback,
            ConfirmStatus.backToDeliveryMethod,
        ),
    [ConfirmStatus.backToPickASeat]: (
        availableCallbacks: Parameters<typeof confirmPage>[0],
        confirmCallback: Parameters<typeof confirmPage>[1],
    ) =>
        confirmPage(
            availableCallbacks,
            confirmCallback,
            ConfirmStatus.backToPickASeat,
        ),
    [ConfirmStatus.backToReservedTicketConfirmation]: (
        availableCallbacks: Parameters<typeof confirmPage>[0],
        confirmCallback: Parameters<typeof confirmPage>[1],
    ) =>
        confirmPage(
            availableCallbacks,
            confirmCallback,
            ConfirmStatus.backToReservedTicketConfirmation,
        ),
    [ConfirmStatus.closeToDeliveryMethod]: (
        availableCallbacks: Parameters<typeof confirmPage>[0],
    ) =>
        confirmPage(
            availableCallbacks,
            ConfirmStatus.closeModal,
            ConfirmStatus.closeToDeliveryMethod,
        ),
    [ConfirmStatus.closeToInterstitial]: (
        availableCallbacks: Parameters<typeof confirmPage>[0],
    ) =>
        confirmPage(
            availableCallbacks,
            ConfirmStatus.toTicketSelectionFromInterstitial,
            ConfirmStatus.closeToInterstitial,
        ),
    [ConfirmStatus.closeForLogin]: (
        availableCallbacks: Parameters<typeof confirmPage>[0],
    ) =>
        confirmPage(
            availableCallbacks,
            ConfirmStatus.closeModal,
            ConfirmStatus.closeForLogin,
        ),
    [ConfirmStatus.closeForPickASeat]: (
        availableCallbacks: Parameters<typeof confirmPage>[0],
    ) =>
        confirmPage(
            availableCallbacks,
            ConfirmStatus.closeModal,
            ConfirmStatus.backToPickASeat,
        ),
    [ConfirmStatus.closeForReservedTicketConfirmation]: (
        availableCallbacks: Parameters<typeof confirmPage>[0],
    ) =>
        confirmPage(
            availableCallbacks,
            ConfirmStatus.closeModal,
            ConfirmStatus.backToReservedTicketConfirmation,
        ),
    [ConfirmStatus.close]: (
        availableCallbacks: Parameters<typeof confirmPage>[0],
    ) => confirmPage(availableCallbacks, ConfirmStatus.closeModal),
    [ConfirmStatus.toCheckout]: (
        availableCallbacks: Parameters<typeof confirmPage>[0],
        confirmCallback: Parameters<typeof confirmPage>[1],
    ) =>
        confirmPage(
            availableCallbacks,
            confirmCallback,
            ConfirmStatus.toCheckout,
        ),
    [EventStatus.canceled]: () => CANCELED_CONTENT,
    [EventStatus.ended]: () => ENDED_CONTENT,
    [EventStatus.soldOut]: () => SOLD_OUT_CONTENT,
    [EventStatus.soldOutInTicketSelection]: () =>
        SOLD_OUT_IN_TICKET_SELECTION_CONTENT,
    [EventStatus.salesEnded]: () => SALES_ENDED_CONTENT,
    [EventStatus.unavailable]: () => EVENT_TICKETS_UNAVAILABLE_CONTENT,
    [SERVER_ERROR_STATUS]: () => SERVER_ERROR_CONTENT,
    [TIME_EXPIRED_STATUS]: timeExpiredContent,
    [TIME_EXPIRED_STATUS_NO_BACK_BUTTON]: timeExpiredContentNoBackButton,
    [WaitlistStatus.error]: () => WAITLIST_ERROR_CONTENT,
    [WaitlistStatus.disabled]: () => WAITLIST_DISABLED_CONTENT,
};

export const SALES_STATUS_CONTENT_MAP = {
    [EventStatus.soldOut]: 'ticketsSoldOut',
    [EventStatus.salesEnded]: 'ticketsWithSalesEnded',
};
